// client/src/components/catalogoModelo/ImportarCatalogoPage.js
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Loader2, Download } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import ImportarCatalogoCard from './ImportarCatalogoCard';
import ImportarCategoriaModal from './ImportarCategoriaModal';
import { useCatalogoModeloItems, useImportarParaConta } from '../../hooks/useCatalogoModelo';
import { useAuth } from '../../context/auth/AuthProvider';

// ✅ Catálogo de Modelos cadastrado pela Matriz — Conta/Franquia navegam e
// clicam "Importar" pra criar uma cópia real (Servico ou ItemVenda) na
// própria account, pré-preenchida (ver server::catalogoModeloController.js
// ::importarParaConta), ou "Importar categoria inteira" pra criar todos os
// itens ativos de uma categoria+tipo de uma vez
// (::importarPorCategoria). Por padrão só mostra itens dos segmentos de
// atuação da própria conta (server filtra por Account.segmentoAtuacao).
const ImportarCatalogoPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const accountId = user?.account?._id || user?.account;

    const [filtroTipo, setFiltroTipo] = useState('');
    const [verTodosSegmentos, setVerTodosSegmentos] = useState(false);
    const [importandoId, setImportandoId] = useState(null);
    const [grupoParaImportar, setGrupoParaImportar] = useState(null);

    const queryParams = useMemo(() => ({
        ...(filtroTipo ? { tipo: filtroTipo } : {}),
        ...(verTodosSegmentos ? { todosSegmentos: 'true' } : {})
    }), [filtroTipo, verTodosSegmentos]);

    const { data, isLoading } = useCatalogoModeloItems(queryParams);
    const importarParaConta = useImportarParaConta();

    const itens = data?.itens || [];

    const grupos = useMemo(() => {
        const mapa = new Map();
        itens.forEach((item) => {
            const chave = `${item.tipo}::${item.categoriaNome}`;
            if (!mapa.has(chave)) {
                mapa.set(chave, { chave, categoriaNome: item.categoriaNome, tipo: item.tipo, itens: [] });
            }
            mapa.get(chave).itens.push(item);
        });
        return Array.from(mapa.values()).sort((a, b) => a.categoriaNome.localeCompare(b.categoriaNome));
    }, [itens]);

    const handleImportar = useCallback((item) => {
        setImportandoId(item._id);
        importarParaConta.mutate(item._id, {
            onSuccess: (res) => {
                const path = res.data.tipo === 'servico' ? '/cadastros/servicos' : '/cadastros/itens-venda';
                navigate(path, { state: { prefillCatalogo: res.data.prefill } });
            },
            onSettled: () => setImportandoId(null)
        });
    }, [importarParaConta, navigate]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Package />}
                    title="Catálogo Onmicare"
                    subtitle={!isLoading ? `${itens.length} item${itens.length !== 1 ? 's' : ''} disponíveis` : null}
                />

                <div className="content-panel">
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label className="form-label">Filtrar por Tipo</label>
                            <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="form-input">
                                <option value="">Todos</option>
                                <option value="servico">Serviço</option>
                                <option value="produto">Produto</option>
                            </select>
                        </div>
                        <div className="form-group-checkbox" style={{ alignSelf: 'end' }}>
                            <input
                                type="checkbox"
                                id="ver-todos-segmentos"
                                checked={verTodosSegmentos}
                                onChange={(e) => setVerTodosSegmentos(e.target.checked)}
                                className="form-checkbox"
                            />
                            <label htmlFor="ver-todos-segmentos">Ver todos os segmentos (não só os meus)</label>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-panel">
                {isLoading ? (
                    <div className="table-loading-state">
                        <Loader2 size={24} className="animate-spin" />
                        <span>Carregando catálogo...</span>
                    </div>
                ) : grupos.length > 0 ? (
                    grupos.map((grupo) => (
                        <div key={grupo.chave} style={{ marginBottom: 32 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h4 className="panel-title">
                                    {grupo.categoriaNome} — {grupo.tipo === 'servico' ? 'Serviços' : 'Produtos'} ({grupo.itens.length})
                                </h4>
                                <button
                                    type="button"
                                    onClick={() => setGrupoParaImportar(grupo)}
                                    className="action-button action-button-secondary"
                                >
                                    <Download size={18} className="icon" /> Importar categoria inteira
                                </button>
                            </div>
                            <div className="image-gallery-grid">
                                {grupo.itens.map((item) => (
                                    <ImportarCatalogoCard
                                        key={item._id}
                                        item={item}
                                        onImportar={handleImportar}
                                        isImporting={importandoId === item._id && importarParaConta.isPending}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="table-empty-state">
                        <Package size={48} />
                        <h3>Nenhum item disponível no catálogo</h3>
                        <p>
                            {verTodosSegmentos
                                ? 'A Matriz ainda não cadastrou itens no catálogo.'
                                : 'Nenhum item nos segmentos de atuação da sua conta. Tente "Ver todos os segmentos".'}
                        </p>
                    </div>
                )}
            </div>

            {grupoParaImportar && (
                <ImportarCategoriaModal
                    grupo={grupoParaImportar}
                    accountId={accountId}
                    onClose={() => setGrupoParaImportar(null)}
                />
            )}
        </Fragment>
    );
};

export default ImportarCatalogoPage;
